import { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import { useQuery } from '@tanstack/react-query'
import { Link as RouterLink } from 'react-router'

import { DataState } from '../../components/DataState'
import { CopyableId } from '../../components/ui/CopyableId'
import { PageHeader } from '../../components/ui/PageHeader'
import { StatusChip } from '../../components/ui/StatusChip'
import { formatDateTime } from '../../format'
import { listAccountCredentials } from '../../api/portal'
import { usePortal } from '../../auth/sessionContext'

const FILTERS = [
  { value: '', label: 'همه' },
  { value: 'active', label: 'فعال' },
  { value: 'revoked', label: 'باطل‌شده' },
] as const

/**
 * Every key on the account, in one place.
 *
 * The question this page exists for is a security question — is anything
 * still able to authenticate that should not be — and it is about the account
 * rather than about one integration. Answering it from the application list
 * meant opening each application in turn and remembering what was in the last
 * one.
 *
 * Read-only on purpose. Issuing, rotating and revoking stay on the
 * application, because each of those is a decision about one integration and
 * the confirmation has to be able to name it. The row links there.
 */
export function CredentialsPage() {
  const { membership } = usePortal()
  const customerId = membership.customer.id
  const [filter, setFilter] = useState<string>('active')

  const query = useQuery({
    // 'account' distinguishes this from the per-application list, which is
    // keyed by an application id under the same prefix so that issuing a
    // credential there invalidates both.
    queryKey: ['client', 'credentials', 'account', customerId],
    queryFn: ({ signal }) => listAccountCredentials(customerId, signal),
  })

  const rows = (query.data ?? []).filter((row) => filter === '' || row.status === filter)

  return (
    <Box>
      <PageHeader
        title="اعتبارنامه‌ها"
        description="هر کلیدی که اپلیکیشن‌های شما با آن به سامانه وصل می‌شوند، همراه با آخرین زمانی که استفاده شده است. ساخت، تعویض و ابطال در صفحهٔ همان برنامه انجام می‌شود."
      />

      <TextField
        select
        size="small"
        label="وضعیت"
        value={filter}
        onChange={(event) => setFilter(event.target.value)}
        sx={{ minWidth: 180, mb: 2 }}
      >
        {FILTERS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <DataState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        isEmpty={rows.length === 0}
        emptyMessage={
          filter === 'active'
            ? 'هیچ اعتبارنامهٔ فعالی وجود ندارد. برای ساخت یکی، به صفحهٔ اپلیکیشنِ موردنظر بروید.'
            : 'اعتبارنامه‌ای با این وضعیت وجود ندارد.'
        }
        onRetry={() => void query.refetch()}
      >
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="اعتبارنامه‌ها">
            <TableHead>
              <TableRow>
                <TableCell>اپلیکیشن</TableCell>
                <TableCell>شناسهٔ کارخواه</TableCell>
                <TableCell>برچسب</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>ساخته‌شده</TableCell>
                <TableCell>آخرین استفاده</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.application_name}</TableCell>
                  <TableCell>
                    <CopyableId value={row.client_id} visible={12} label="شناسهٔ کارخواه" />
                  </TableCell>
                  <TableCell>{row.label || '—'}</TableCell>
                  <TableCell>
                    <StatusChip status={row.status} />
                  </TableCell>
                  <TableCell>{formatDateTime(row.created_at)}</TableCell>
                  {/* Never used is a different fact from used long ago, and it
                      is the one that says a key can be revoked safely. */}
                  <TableCell>
                    {row.last_used_at ? formatDateTime(row.last_used_at) : 'هرگز استفاده نشده'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      component={RouterLink}
                      to={`/applications/${row.application}`}
                    >
                      مدیریت
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataState>
    </Box>
  )
}
